import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { GeneralComponentClass, Service } from 'webapp-types';
import { MetricLibrarySortBy, TemplateObjectType } from 'webapp/enums/enums';
import { copyObject, uniq } from 'webapp/utils/common';
import { sortByConnectionStatus } from 'webapp/utils/metric-sort';

import didInsert from 'webapp/modifiers/did-insert';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import FilterLineComponent from 'webapp/components/filter/line';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import type { Args, SectionBase } from 'webapp/components/metric-library/base';
import MetricLibraryBaseComponent from 'webapp/components/metric-library/base';
import MetricLibraryConnectedIntegrationsComponent from 'webapp/components/metric-library/connected-integrations';
import MetricLibraryExtraActionsComponent from 'webapp/components/metric-library/extra-actions';
import type { Args as GridBaseArgs } from 'webapp/components/metric-library/grid';
import MetricLibraryGridMetricsComponent from 'webapp/components/metric-library/grid/metrics';
import MetricLibraryGridSourcesComponent from 'webapp/components/metric-library/grid/sources';
import MetricLibraryGridTemplatesComponent from 'webapp/components/metric-library/grid/templates';
import MetricLibraryPopularIntegrationsComponent from 'webapp/components/metric-library/popular-integrations';
import MetricLibraryPopularMetricsComponent from 'webapp/components/metric-library/popular-metrics';

import FilterSortMetricLibraryMainIntegrations from 'webapp/models-custom/filter-sort/metric-library-main-integrations';
import FilterSortMetricLibraryMainMetrics from 'webapp/models-custom/filter-sort/metric-library-main-metrics';
import FilterSortMetricLibraryMainTemplates from 'webapp/models-custom/filter-sort/metric-library-main-templates';

export type FilterSortMetricLibraryIndex =
	| FilterSortMetricLibraryMainMetrics
	| FilterSortMetricLibraryMainIntegrations
	| FilterSortMetricLibraryMainTemplates;

export const enum Section {
	METRICS = 'metrics',
	SOURCES = 'sources',
	BOARD_TEMPLATES = 'board-templates',
	REPORT_TEMPLATES = 'report-templates',
}

const sectionOptions: SectionBase[] = [
	{
		id: Section.METRICS,
		title: 'Metrics',
		initialRowCount: 2,
		loadMoreRowCount: 4,
	},
	{
		id: Section.SOURCES,
		title: 'Integrations',
		initialRowCount: 2,
	},
	{
		id: Section.BOARD_TEMPLATES,
		title: 'Dashboard templates',
		initialRowCount: 1,
	},
	{
		id: Section.REPORT_TEMPLATES,
		title: 'Report templates',
		initialRowCount: 1,
	},
];

type SectionOption = GridBaseArgs & {
	filterSort: FilterSortMetricLibraryIndex;
	componentClass: GeneralComponentClass;
};

export default class MetricLibraryIndexComponent extends MetricLibraryBaseComponent<Args> {
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	@tracked sections: SectionOption[] = [];

	mainFilterSort: FilterSortMetricLibraryMainMetrics | null = null;
	allFilterSorts: {
		filterSort: FilterSortMetricLibraryIndex;
		rootElementSelector: string;
		preventBeforeFilter?: boolean;
	}[] = [];

	quickSearchOptions = ['Sessions', 'MRR', 'Marketing', 'HubSpot'];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSort = {
			[Section.METRICS]: new FilterSortMetricLibraryMainMetrics({
				getAllItems: () => {
					return this.mmMetricsService.metrics;
				},
				beforeFilter: () => {
					const newFiltersSelectedValues = filterSort[Section.METRICS].filters.map(
						(filter) => filter.selected,
					);

					this.allFilterSorts.forEach((otherFilterSort) => {
						if (!otherFilterSort.preventBeforeFilter) {
							otherFilterSort.filterSort.filters.forEach((filter, index) => {
								// To prevent "You attempted to update `_selected` on `Filter`, but it had already been used previously in the same computation."
								next(() => {
									const updateSiblingFilter =
										!filter.isSort ||
										!filterSort[Section.METRICS].isSortBySearchPointsArray;

									if (updateSiblingFilter) {
										filter.selectOption(newFiltersSelectedValues[index] || []);
									}
								});
							});

							otherFilterSort.filterSort.typedFilter =
								filterSort[Section.METRICS].typedFilter;
						}
					});
				},
			}),
			[Section.SOURCES]: new FilterSortMetricLibraryMainIntegrations({
				getAllItems: () => {
					return this.connectableSingleSourceTypes;
				},
			}),
			[Section.BOARD_TEMPLATES]: new FilterSortMetricLibraryMainTemplates({
				getAllItems: () => {
					return this.publicBoardTemplates;
				},
			}),
			[Section.REPORT_TEMPLATES]: new FilterSortMetricLibraryMainTemplates({
				getAllItems: () => {
					return this.publicReportTemplates;
				},
			}),
		};

		sectionOptions.forEach((option, index) => {
			const isMainFilterSort = index === 0,
				optionFilterSort = filterSort[option.id as Section];
			optionFilterSort.additionalSorts = isMainFilterSort ? [sortByConnectionStatus] : [];

			this.allFilterSorts.push({
				filterSort: optionFilterSort,
				rootElementSelector: `.section.${option.id} ul`,
				preventBeforeFilter: isMainFilterSort,
			});

			if (isMainFilterSort) {
				this.mainFilterSort = optionFilterSort as FilterSortMetricLibraryMainMetrics;
			}

			const section: SectionOption = {
				filterSort: optionFilterSort,
				classNames: option.id + ' lazy-grid',
				...copyObject(option),
				componentClass: this.getComponentClass(option.id as Section),
			};

			this.sections.push(section);
		});
	}

	@cached
	get metrics() {
		return this.mmMetricsService.metrics;
	}

	@cached
	get metricsByPopularity() {
		const connectedSourceTypeKeys = uniq(
			this.mmSourcesService.sourceModels.map((s) => s.sourceType?.key),
		);

		return this.mmMetricsService.metricsByPopularity.filter((m) => {
			return m.isOnSource || !connectedSourceTypeKeys.includes(m.dataSourceTypeKey);
		});
	}

	@cached
	get connectableSingleSourceTypes() {
		return this.mmSourceTypesService.connectableSourceTypeModels.filter(
			(st) => !st.isGroup && !st.isHidden,
		);
	}

	@cached
	get publicTemplates() {
		return this.mmTemplatesService.publicTemplateModels;
	}

	@cached
	get publicBoardTemplates() {
		return this.publicTemplates.filter(
			(template) => template.objectType === TemplateObjectType.BOARD,
		);
	}

	@cached
	get publicReportTemplates() {
		return this.publicTemplates.filter(
			(template) => template.objectType === TemplateObjectType.REPORT,
		);
	}

	@cached
	get connectedIntegrations() {
		return this.mmSourceTypesService.connectableSourceTypeModels.filter(
			(st) =>
				!st.isGroup &&
				!st.isHidden &&
				st.isAlreadyConnected &&
				(!st.isToken || !st.connectedSources.every((source) => source.isRoot)), // we want to show only connected custom token sources that doesn't have root key
		);
	}

	get showConnectedIntegrationsSection() {
		return this.connectedIntegrations.length > 0;
	}

	get metricsSection() {
		const allSections = this.sections;
		if (allSections.length) {
			return allSections[0];
		}

		return null;
	}

	get showMetricsSection() {
		return !!this.metricsSection && this.metricsSection.filterSort.results.length > 0;
	}

	get otherSections() {
		const allSections = this.sections;
		if (allSections.length) {
			return allSections.slice(1);
		}

		return [] as SectionOption[];
	}

	get isSearchOrFilterInactive(): boolean {
		const mainFilterSort = this.mainFilterSort;
		if (mainFilterSort) {
			const sortByFilterSelected = (mainFilterSort.findFilter(3)?.selected || [])[0];
			return (
				!mainFilterSort.typedFilter &&
				!mainFilterSort.isAnyNonSortFilterActive &&
				sortByFilterSelected === MetricLibrarySortBy.POPULARITY
			);
		} else {
			return true;
		}
	}

	get noSearchResults(): boolean {
		return (
			!this.isSearchOrFilterInactive &&
			this.allFilterSorts.every((filter) => filter.filterSort.noResults)
		);
	}

	private getComponentClass(sectionId: Section): GeneralComponentClass {
		switch (sectionId) {
			case Section.METRICS:
				return MetricLibraryGridMetricsComponent;
			case Section.SOURCES:
				return MetricLibraryGridSourcesComponent;
			default:
				return MetricLibraryGridTemplatesComponent;
		}
	}

	@action
	quickSearch(quickSearchOption: string) {
		if (this.mainFilterSort) {
			this.mainFilterSort.typedFilter = quickSearchOption;
		}
	}

	@action
	setupView(node: HTMLElement) {
		node.querySelector<HTMLInputElement>('.input-search input')?.focus();
	}
	<template>
		<div class="metric-library-container bottom-padding" {{didInsert this.setupView}}>

			<div class="header">
				<div class="header__title">Metric library</div>
				{{#if this.mainFilterSort}}
					<div class="header__search">
						<FilterLineComponent @model={{this.mainFilterSort}} />
					</div>
				{{/if}}
				<div class="header__quick-search">
					<span>or try quick search instead</span>
					<div class="header__quick-search-tags">
						{{#each this.quickSearchOptions as |quickSearchOption|}}
							<a role="button" {{on "click" (fn this.quickSearch quickSearchOption)}}>
								{{quickSearchOption}}
							</a>
						{{/each}}
					</div>
				</div>
			</div>

			{{#if this.isSearchOrFilterInactive}}
				<MetricLibraryPopularMetricsComponent
					@batchSelect={{this.batchSelect}}
					@metricsByPopularity={{this.metricsByPopularity}}
				/>

				{{#if this.showConnectedIntegrationsSection}}
					<MetricLibraryConnectedIntegrationsComponent
						@connectedIntegrations={{this.connectedIntegrations}}
					/>
				{{/if}}

				<MetricLibraryPopularIntegrationsComponent
					@showConnectedIntegrationsSection={{this.showConnectedIntegrationsSection}}
				/>
			{{else}}
				{{#if this.noSearchResults}}
					{{#if this.mainFilterSort}}
						<FilterNoResultsComponent @model={{this.mainFilterSort}} />
					{{/if}}
					<MetricLibraryExtraActionsComponent />
				{{/if}}
				<div class="ml-sections">
					{{#let this.metricsSection as |metricsSection|}}
						{{#if metricsSection}}
							{{#if metricsSection.filterSort.results.length}}
								{{#let
									(component metricsSection.componentClass)
									as |ComponentName|
								}}
									<ComponentName
										@filterSort={{metricsSection.filterSort}}
										@title={{metricsSection.title}}
										@initialRowCount={{metricsSection.initialRowCount}}
										@loadMoreRowCount={{metricsSection.loadMoreRowCount}}
										@classNames={{metricsSection.classNames}}
										@batchSelect={{this.batchSelect}}
									/>
								{{/let}}
							{{/if}}
						{{/if}}
					{{/let}}
					{{#each this.otherSections as |section|}}
						{{#if section.filterSort.results}}
							{{#let (component section.componentClass) as |ComponentName|}}
								<ComponentName
									@filterSort={{section.filterSort}}
									@title={{section.title}}
									@initialRowCount={{section.initialRowCount}}
									@classNames={{section.classNames}}
								/>
							{{/let}}
						{{/if}}
					{{/each}}
				</div>
			{{/if}}
		</div>

		{{! if we don't use results, only in the Metric library *get results* in app\models-custom\filter-sort\base.ts would be consumed for the first time with a search term already, which causes it to sort the items wrongly }}
		{{#each this.sections as |section|}}
			{{#if section.filterSort.results}}
				{{! Do not remove }}
			{{/if}}
		{{/each}}

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
